import React, { useState, useEffect } from 'react';
import { getUsers, createUser, updateUser, deleteUser, resetUserPassword, resetUser2FA, getCollectorStats, aggiornaStatistiche, importText } from '../services/api';
import { useAuth } from '../context/AuthContext';

const Admin = () => {
  const { user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('utenti');
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUserForm, setShowUserForm] = useState(false);
  const [importData, setImportData] = useState('');
  const [message, setMessage] = useState(null);

  // Form nuovo utente
  const [newUsername, setNewUsername] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newIsAdmin, setNewIsAdmin] = useState(false);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'utenti') {
        const res = await getUsers();
        setUsers(res.data);
      } else if (activeTab === 'dati') {
        const res = await getCollectorStats();
        setStats(res.data);
      }
    } catch (err) {
      console.error('Errore:', err);
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await createUser({
        username: newUsername,
        email: newEmail,
        password: newPassword,
        is_admin: newIsAdmin
      });
      setShowUserForm(false);
      setNewUsername('');
      setNewEmail('');
      setNewPassword('');
      setNewIsAdmin(false);
      showMessage('Utente creato con successo');
      loadData();
    } catch (err) {
      showMessage(err.response?.data?.detail || 'Errore creazione utente', 'error');
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await updateUser(user.id, { is_active: !user.is_active });
      showMessage(`Utente ${user.is_active ? 'disattivato' : 'attivato'}`);
      loadData();
    } catch (err) {
      showMessage('Errore aggiornamento', 'error');
    }
  };

  const handleResetPassword = async (userId) => {
    const newPwd = prompt('Nuova password:');
    if (!newPwd) return;
    
    try {
      await resetUserPassword(userId, newPwd);
      showMessage('Password resettata');
    } catch (err) {
      showMessage('Errore reset password', 'error');
    }
  };

  const handleReset2FA = async (userId) => {
    if (!window.confirm('Resettare il 2FA per questo utente?')) return;
    
    try {
      await resetUser2FA(userId);
      showMessage('2FA resettato');
      loadData();
    } catch (err) {
      showMessage('Errore reset 2FA', 'error');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Eliminare questo utente?')) return;
    
    try {
      await deleteUser(userId);
      showMessage('Utente eliminato');
      loadData();
    } catch (err) {
      showMessage(err.response?.data?.detail || 'Errore eliminazione', 'error');
    }
  };

  const handleImport = async () => {
    if (!importData.trim()) {
      showMessage('Inserisci i dati da importare', 'error');
      return;
    }
    
    try {
      const res = await importText(importData);
      showMessage(res.data.message);
      setImportData('');
      loadData();
    } catch (err) {
      showMessage(err.response?.data?.detail || 'Errore import', 'error');
    }
  };

  const handleAggiornaStats = async () => {
    try {
      await aggiornaStatistiche();
      showMessage('Statistiche aggiornate');
      loadData();
    } catch (err) {
      showMessage('Errore aggiornamento statistiche', 'error');
    }
  };

  return (
    <div>
      <h2 style={{ marginBottom: '20px' }}>Amministrazione</h2>

      {message && (
        <div className={`alert alert-${message.type === 'error' ? 'error' : 'success'}`}>
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div className="tabs">
        <div 
          className={`tab ${activeTab === 'utenti' ? 'active' : ''}`}
          onClick={() => setActiveTab('utenti')}
        >
          👥 Utenti
        </div>
        <div 
          className={`tab ${activeTab === 'dati' ? 'active' : ''}`}
          onClick={() => setActiveTab('dati')}
        >
          📊 Dati
        </div>
      </div>

      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : (
        <>
          {/* Tab Utenti */}
          {activeTab === 'utenti' && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Gestione Utenti</h3>
                <button className="btn btn-primary" onClick={() => setShowUserForm(!showUserForm)}>
                  {showUserForm ? '✕ Chiudi' : '➕ Nuovo Utente'}
                </button>
              </div>

              {showUserForm && (
                <form onSubmit={handleCreateUser} style={{ marginBottom: '20px', padding: '15px', background: '#f7fafc', borderRadius: '8px' }}>
                  <div className="grid grid-4">
                    <div className="form-group">
                      <label className="form-label">Username</label>
                      <input
                        type="text"
                        className="form-input"
                        value={newUsername}
                        onChange={(e) => setNewUsername(e.target.value)}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        className="form-input"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Password</label>
                      <input
                        type="password"
                        className="form-input"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Admin</label>
                      <select
                        className="form-select"
                        value={newIsAdmin}
                        onChange={(e) => setNewIsAdmin(e.target.value === 'true')}
                      >
                        <option value="false">No</option>
                        <option value="true">Sì</option>
                      </select>
                    </div>
                  </div>
                  <button type="submit" className="btn btn-success">Crea Utente</button>
                </form>
              )}

              <table className="table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Admin</th>
                    <th>2FA</th>
                    <th>Stato</th>
                    <th>Ultimo Login</th>
                    <th>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id}>
                      <td><strong>{u.username}</strong></td>
                      <td>{u.email}</td>
                      <td>{u.is_admin ? '✅' : '❌'}</td>
                      <td>{u.totp_enabled ? '✅' : '❌'}</td>
                      <td>
                        <span 
                          className="ruota-badge"
                          style={{ 
                            background: u.is_active ? '#c6f6d5' : '#fed7d7',
                            color: u.is_active ? '#22543d' : '#822727'
                          }}
                        >
                          {u.is_active ? 'Attivo' : 'Disattivato'}
                        </span>
                      </td>
                      <td>
                        {u.last_login 
                          ? new Date(u.last_login).toLocaleString('it-IT')
                          : 'Mai'
                        }
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '5px' }}>
                          {u.id !== currentUser?.id && (
                          <button
                            className="btn btn-secondary"
                            style={{ padding: '3px 8px', fontSize: '0.75rem' }}
                            onClick={() => handleToggleActive(u)}
                            title={u.is_active ? 'Disattiva' : 'Attiva'}
                          >
                            {u.is_active ? 'OFF' : 'ON'}
                          </button>
                          )}
                          <button 
                            className="btn btn-secondary" 
                            style={{ padding: '3px 8px', fontSize: '0.75rem' }}
                            onClick={() => handleResetPassword(u.id)}
                            title="Reset Password"
                          >
                            🔑
                          </button>
                          {u.totp_enabled && (
                            <button 
                              className="btn btn-secondary" 
                              style={{ padding: '3px 8px', fontSize: '0.75rem' }}
                              onClick={() => handleReset2FA(u.id)}
                              title="Reset 2FA"
                            >
                              📱
                            </button>
                          )}
                          {u.id !== currentUser?.id && (
                          <button
                            className="btn btn-danger"
                            style={{ padding: '3px 8px', fontSize: '0.75rem' }}
                            onClick={() => handleDeleteUser(u.id)}
                            title="Elimina"
                          >
                            🗑️
                          </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Tab Dati */}
          {activeTab === 'dati' && (
            <>
              <div className="card" style={{ marginBottom: '20px' }}>
                <h3 className="card-title" style={{ marginBottom: '20px' }}>Statistiche Database</h3>
                
                <div className="grid grid-4">
                  <div className="stat-card">
                    <div className="stat-value">{stats?.totale || 0}</div>
                    <div className="stat-label">Estrazioni Totali</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{stats?.estrazioni_per_data || 0}</div>
                    <div className="stat-label">Giorni di Estrazione</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value" style={{ fontSize: '1rem' }}>{stats?.prima_data || '-'}</div>
                    <div className="stat-label">Prima Estrazione</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value" style={{ fontSize: '1rem' }}>{stats?.ultima_data || '-'}</div>
                    <div className="stat-label">Ultima Estrazione</div>
                  </div>
                </div>

                <div style={{ marginTop: '20px' }}>
                  <button className="btn btn-primary" onClick={handleAggiornaStats}>
                    🔄 Aggiorna Statistiche
                  </button>
                </div>
              </div>

              <div className="card">
                <h3 className="card-title" style={{ marginBottom: '20px' }}>Import Estrazioni</h3>
                <p style={{ color: '#718096', marginBottom: '15px' }}>
                  Formato: DATA;RUOTA;N1;N2;N3;N4;N5 (una riga per estrazione)
                  <br />
                  Esempio: 2024-12-31;BARI;5;17;43;77;85
                </p>
                
                <div className="form-group">
                  <textarea
                    className="form-input"
                    rows={10}
                    value={importData}
                    onChange={(e) => setImportData(e.target.value)}
                    placeholder="Incolla qui i dati delle estrazioni..."
                  />
                </div>
                
                <button className="btn btn-success" onClick={handleImport}>
                  📥 Importa Dati
                </button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Admin;
